import { createRoute, Link, useNavigate } from "@tanstack/react-router";
import { withPermission } from "../lib/with-permission";
import { useTranslation } from "react-i18next";
import { useState, useCallback, useEffect } from "react";
import type { SearchableSelectOption } from "../components/ui/SearchableSelect/SearchableSelect";
import { rootRoute } from "./__root";
import { PageHeader } from "../components/ui/PageHeader/PageHeader";
import { Card } from "../components/ui/Card/Card";
import { Table } from "../components/ui/Table/Table";
import { Button } from "../components/ui/Button/Button";
import { Input } from "../components/ui/Input/Input";
import { Select } from "../components/ui/Select/Select";
import { FormField } from "../components/ui/FormField/FormField";
import { Modal } from "../components/ui/Modal/Modal";
import { SearchableSelect } from "../components/ui/SearchableSelect/SearchableSelect";
import { StatusBadge } from "../components/ui/StatusBadge/StatusBadge";
import { LoadingSpinner } from "../components/ui/LoadingSpinner/LoadingSpinner";
import { ErrorFallback } from "../components/ui/ErrorFallback/ErrorFallback";
import { Pagination } from "../components/ui/Pagination/Pagination";
import { FilterBar } from "../components/ui/FilterBar/FilterBar";
import { useActivityList, useCreateActivity } from "../hooks/use-activities";
import { api } from "../lib/api";
import { formatDate } from "../lib/date";
import styles from "./Activities.module.css";

export const route = createRoute({
	getParentRoute: () => rootRoute,
	path: "/activities",
	component: withPermission(ActivitiesPage, "activities", "read"),
	validateSearch: (search: Record<string, unknown>) => ({
		page: Number(search.page) || 1,
		size: Number(search.size) || 20,
		type: String(search.type || ""),
		search: String(search.search || ""),
		createdAtFrom: String(search.createdAtFrom || ""),
		createdAtTo: String(search.createdAtTo || ""),
		companyId: search.companyId ? String(search.companyId) : undefined,
		personId: search.personId ? String(search.personId) : undefined,
	}),
});

function ActivitiesPage() {
	const { t } = useTranslation();
	const navigate = useNavigate({ from: "/activities" });
	const urlSearch = route.useSearch();
	const {
		page,
		size,
		type: typeFilter,
		companyId,
		personId,
		search: searchQuery,
		createdAtFrom,
		createdAtTo,
	} = urlSearch;

	const setPage = (p: number) => navigate({ search: (prev) => ({ ...prev, page: p }) });
	const setPageSize = (s: number) =>
		navigate({ search: (prev) => ({ ...prev, size: s, page: 1 }) });
	const [createOpen, setCreateOpen] = useState(false);
	const [form, setForm] = useState({
		type: "call" as const,
		subject: "",
		content: "",
		dueDate: "",
		companyId: companyId ?? "",
		personId: personId ?? "",
	});
	const [formError, setFormError] = useState("");
	const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
	const [initialOptions, setInitialOptions] = useState<Record<string, SearchableSelectOption>>({});

	useEffect(() => {
		const opts: Record<string, SearchableSelectOption> = {};
		const fetches: Promise<void>[] = [];
		if (companyId) {
			fetches.push(
				api
					.get<{ id: string; legalName: string }>(`/api/companies/${companyId}`)
					.then((c) => {
						opts.company = { value: c.id, label: c.legalName };
					})
					.catch(() => {}),
			);
		}
		if (personId) {
			fetches.push(
				api
					.get<{ id: string; firstName: string; lastName: string }>(`/api/persons/${personId}`)
					.then((p) => {
						opts.person = { value: p.id, label: `${p.firstName} ${p.lastName}` };
					})
					.catch(() => {}),
			);
		}
		Promise.all(fetches).then(() => {
			setInitialOptions(opts);
			if (companyId || personId) {
				setCreateOpen(true);
			}
		});
	}, [companyId, personId]);
	const { data, isLoading, isError, refetch } = useActivityList({
		type: typeFilter || undefined,
		search: searchQuery || undefined,
		createdAtFrom: createdAtFrom || undefined,
		createdAtTo: createdAtTo || undefined,
		page,
		limit: size,
	});
	const createActivity = useCreateActivity();

	const activities = data?.items ?? [];
	const total = data?.total ?? 0;

	const typeOptions = [
		{ value: "", label: t("all") },
		{ value: "call", label: t("call") },
		{ value: "meeting", label: t("meeting") },
		{ value: "email", label: t("email") },
		{ value: "note", label: t("note") },
		{ value: "task", label: t("task") },
		{ value: "whatsapp", label: t("whatsapp") },
	];

	const loadCompanies = useCallback(async (query: string) => {
		const res = await api.get<{ items: Array<{ id: string; legalName: string }> }>(
			`/api/companies?limit=50${query ? `&search=${encodeURIComponent(query)}` : ""}`,
		);
		return res.items.map((c) => ({ value: c.id, label: c.legalName }));
	}, []);

	const loadPersons = useCallback(
		async (query: string) => {
			const res = await api.get<{
				items: Array<{ id: string; firstName: string; lastName: string }>;
			}>(
				`/api/persons?limit=50${form.companyId ? `&companyId=${form.companyId}` : ""}${query ? `&search=${encodeURIComponent(query)}` : ""}`,
			);
			return res.items.map((p) => ({ value: p.id, label: `${p.firstName} ${p.lastName}` }));
		},
		[form.companyId],
	);

	const submit = () => {
		setFormError("");
		const errors: Record<string, string> = {};
		if (!form.type) errors.type = t("requiredField");
		if (!form.subject?.trim()) errors.subject = t("requiredField");
		setFieldErrors(errors);
		if (Object.keys(errors).length > 0) return;
		createActivity.mutate(
			{
				...form,
				dueDate: form.dueDate || undefined,
				companyId: form.companyId || undefined,
				personId: form.personId || undefined,
			},
			{
				onSuccess: () => {
					setCreateOpen(false);
					setForm({
						type: "call",
						subject: "",
						content: "",
						dueDate: "",
						companyId: "",
						personId: "",
					});
					setFormError("");
					alert(t("createSuccess"));
				},
				onError: (err) => {
					setFormError(err instanceof Error ? err.message : t("createFailed"));
				},
			},
		);
	};

	return (
		<div>
			<PageHeader
				title={t("activities")}
				action={<Button onClick={() => setCreateOpen(true)}>{t("createActivity")}</Button>}
			/>

			<div className={styles.filters}>
				<FilterBar
					filters={[
						{ key: "search", type: "text", label: t("search") },
						{ key: "createdAt", type: "dateRange", label: t("createdAt") },
						{
							key: "type",
							type: "select",
							label: t("type"),
							options: typeOptions.filter((o) => o.value !== ""),
						},
					]}
					values={{ search: searchQuery, createdAtFrom, createdAtTo, type: typeFilter }}
					onChange={(vals) => {
						navigate({
							search: (prev) => ({
								...prev,
								search: vals.search || "",
								createdAtFrom: vals.createdAtFrom || "",
								createdAtTo: vals.createdAtTo || "",
								type: vals.type || "",
								page: 1,
							}),
						});
					}}
				/>
			</div>

			<Card>
				{isLoading && <LoadingSpinner />}
				{isError && <ErrorFallback onRetry={refetch} />}
				{!isLoading && !isError && (
					<Table
						columns={[
							{
								key: "type",
								header: t("type"),
								render: (row) => <StatusBadge status={row.type} type="activity" />,
							},
							{ key: "subject", header: t("subject") },
							{
								key: "dueDate",
								header: t("dueDate"),
								render: (row) => (row.dueDate ? formatDate(row.dueDate) : "-"),
							},
							{
								key: "isCompleted",
								header: t("isCompleted"),
								render: (row) => (row.isCompleted ? t("yes") : t("no")),
							},
							{
								key: "actions",
								header: t("actions"),
								render: (row) => (
									<Link to={`/activities/${row.id}` as never} className={styles.link}>
										{t("viewDetails")}
									</Link>
								),
							},
						]}
						data={activities}
						keyExtractor={(row) => row.id}
					/>
				)}
				<Pagination
					page={page}
					pageSize={size}
					total={total}
					onPageChange={setPage}
					onPageSizeChange={setPageSize}
				/>
			</Card>

			<Modal
				isOpen={createOpen}
				onClose={() => {
					setCreateOpen(false);
					navigate({
						to: ".",
						search: {
							page: 1,
							size: 20,
							type: "",
							search: "",
							createdAtFrom: "",
							createdAtTo: "",
							companyId: undefined,
							personId: undefined,
						},
					});
				}}
				title={t("createActivity")}
			>
				{formError && <div className={styles.formError}>{formError}</div>}
				<FormField label={t("type")} required>
					<Select
						options={typeOptions.filter((o) => o.value !== "")}
						value={form.type}
						onChange={(e) => setForm({ ...form, type: e.target.value as never })}
						error={fieldErrors.type ?? ""}
					/>
				</FormField>
				<FormField label={t("company")}>
					<SearchableSelect
						value={form.companyId}
						onChange={(val) => setForm({ ...form, companyId: val, personId: "" })}
						loadOptions={loadCompanies}
						initialOption={initialOptions.company}
						placeholder={t("selectCompany")}
						clearable
					/>
				</FormField>
				<FormField label={t("contactPersons")}>
					<SearchableSelect
						value={form.personId}
						onChange={(val) => setForm({ ...form, personId: val })}
						loadOptions={loadPersons}
						initialOption={initialOptions.person}
						placeholder={t("selectPerson")}
						clearable
						disabled={!form.companyId}
					/>
				</FormField>
				<FormField label={t("subject")} required>
					<Input
						value={form.subject}
						onChange={(e) => setForm({ ...form, subject: e.target.value })}
						error={fieldErrors.subject}
					/>
				</FormField>
				<FormField label={t("content")}>
					<Input
						value={form.content}
						onChange={(e) => setForm({ ...form, content: e.target.value })}
					/>
				</FormField>
				<FormField label={t("dueDate")}>
					<Input
						type="date"
						value={form.dueDate}
						onChange={(e) => setForm({ ...form, dueDate: e.target.value })}
					/>
				</FormField>
				<div className={styles.modalActions}>
					<Button variant="ghost" onClick={() => setCreateOpen(false)}>
						{t("cancel")}
					</Button>
					<Button onClick={submit} isLoading={createActivity.isPending}>
						{t("create")}
					</Button>
				</div>
			</Modal>
		</div>
	);
}
